import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles, BarChart3, Zap, Star, DollarSign, ChevronRight, Check,
} from 'lucide-react';

const STEPS = [
  {
    icon: Sparkles,
    title: 'Welcome to Stokora',
    body: 'A flight simulator for swing trading. We scan thousands of stocks every day, score the best setups across 13 indicators, and let you trade them with mock money — so you can build a real track record without risking a rupee or a dollar.',
    next: 'Show me around',
  },
  {
    icon: BarChart3,
    title: 'Dashboard — your daily briefing',
    body: 'A one-screen story: a plain-English headline summarising today\'s market, the four major indices and their charts, how Stokora\'s signal engine has been doing lately (rolling 30-day win rate), and where YOU stand — your paper-trading balance and P&L. A compact "Signal engine" tile shows today\'s top 3 picks; full cards live on the Signals tab.',
    next: 'Next: Signals',
  },
  {
    icon: Zap,
    title: 'Signals — the ideas of the day',
    body: 'The Signals tab ranks the top 25 trade setups for your market. Each card shows direction (LONG/SHORT), a confidence score, target & stop prices, and the reasoning. Every signal is automatically backtested against 90 days of real price history — cards flip from VALIDATING to VALIDATED as the replay finishes, so what you see is data-backed by default.',
    next: 'Next: Watchlist',
  },
  {
    icon: Star,
    title: 'Watchlist — your personal radar',
    body: 'Save tickers you care about. Each gets scored every time you load the page using the same engine that powers Signals — so you always have a fresh read on your favourites.',
    next: 'Next: Trading',
  },
  {
    icon: DollarSign,
    title: 'Paper trade with mock money',
    body: 'Take the signals you trust and act on them. Every account starts with $10,000. Every open position is monitored live — if conditions reverse, you\'ll get a Signal Health alert in-app and by email. Over weeks, you build a personal track record.',
    next: 'Get started',
  },
];

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(8, 12, 20, 0.85)',
  backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
  animation: 'tour-fade 0.25s ease-out',
};

const cardStyle = {
  background: 'var(--bg-card)',
  borderRadius: 14,
  border: '1px solid rgba(91,106,240,0.35)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  padding: '28px 30px',
  maxWidth: 460,
  width: '100%',
  color: 'var(--text-primary)',
  animation: 'tour-slide 0.3s ease-out',
};

const TOUR_CSS = `
  @keyframes tour-fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes tour-slide { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .tour-btn-primary { transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .tour-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(91,106,240,0.35); }
`;

export default function ProductTour() {
  const { user, dismissTour } = useAuth();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  if (!user || user.has_seen_tour || closing) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  const next = () => {
    if (isLast) {
      setClosing(true);
      dismissTour();
    } else {
      setStep(step + 1);
    }
  };

  const skip = () => {
    setClosing(true);
    dismissTour();
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <style>{TOUR_CSS}</style>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(91,106,240,0.18), rgba(124,58,237,0.14))',
            border: '1px solid rgba(91,106,240,0.35)',
          }}>
            <Icon size={18} color="var(--accent)" />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Step {step + 1} of {STEPS.length}
            </div>
            <h2 id="tour-title" style={{ margin: '2px 0 0', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              {s.title}
            </h2>
          </div>
        </div>

        <p style={{ margin: '0 0 18px', fontSize: 13, lineHeight: 1.65, color: 'var(--text-secondary)' }}>
          {s.body}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 18 }}>
          {STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                flex: 1, height: 3, borderRadius: 2,
                background: i <= step ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
                transition: 'background 0.2s ease',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <button
            onClick={skip}
            style={{
              background: 'transparent', border: 'none', color: 'var(--text-muted)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', padding: '6px 4px',
            }}
          >
            Skip
          </button>
          <button
            onClick={next}
            className="tour-btn-primary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 18px',
              background: 'linear-gradient(135deg, #6366f1, #7c3aed)',
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {s.next}
            {isLast ? <Check size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
